// Single Source of Truth für das Release-1-Tastatur-Set des Mail-Cockpits (X1).
//
// Doc 04 (`docs/mail-parity-research/04-tastatur-sichtbarkeit-doktrin.md`) ist die
// Autorität; diese Liste ist die EINE maschinenlesbare Fassung davon. Overlay,
// Button-Tooltips und Palette-`hint`s lesen NUR hier (DRY) — es gibt keinen
// zweiten Ort, an dem eine Taste als Freitext steht (keine Divergenz). Das R1-Set
// ist abschließend: kein zusätzliches Kürzel erfinden.
//
// Die BESCHREIBUNG einer Aktion („was tut die Taste") ist Oberfläche und liegt
// deshalb im Katalog (`i18n/*/mail.ts::kuerzel.aktion`) — hier stehen nur noch
// Aktion, Tasten und die Reihenfolge. Die TASTEN selbst bleiben unübersetzt:
// sie sind die Bindung, und eine übersetzte Taste wäre eine, die es nicht gibt.
import { katalog } from "../i18n/lang";

export type ShortcutAction =
  | "delete"
  | "space"
  | "palette"
  | "search"
  | "termin"
  | "help";

export interface Shortcut {
  action: ShortcutAction;
  /** Anzeige-Tasten (Reihenfolge = Anzeige). Erster Eintrag = Primär-Hint. */
  keys: string[];
  /** Optionaler Zusatz (z. B. die Editor-Sonderbindung der Palette) — der
   *  Schlüssel des Textes im Mail-Katalog, nicht der Text selbst. */
  note?: "notizPalette";
}

// Reihenfolge = Overlay-Reihenfolge (Doc 04 / Driver D6): Löschen · Blättern ·
// Palette · Suche · Termin · Übersicht.
export const SHORTCUTS: Shortcut[] = [
  { action: "delete", keys: ["Entf"] },
  {
    action: "space",
    keys: ["Leertaste", "Umschalt+Leertaste"],
  },
  {
    action: "palette",
    keys: ["Alt+Q"],
    note: "notizPalette",
  },
  // NUR F3 — und dabei bleibt es. `Strg+E` war hier früher die zweite Bindung
  // und ist BEWUSST entfernt: Chrome belegt es selbst mit dem Sprung in die
  // Adress-/Suchleiste, ein App-Kürzel darauf nimmt dem Nutzer eine
  // Browser-Funktion weg. Nicht als Lücke wieder auffüllen (Doc 04 vermerkt es).
  { action: "search", keys: ["F3"] },
  { action: "termin", keys: ["Strg+Umschalt+A"] },
  { action: "help", keys: ["F1", "?"] },
];

const BY_ACTION: Record<ShortcutAction, Shortcut> = SHORTCUTS.reduce(
  (acc, s) => {
    acc[s.action] = s;
    return acc;
  },
  {} as Record<ShortcutAction, Shortcut>
);

/** Primärer Tastenhinweis einer Aktion (Button-`title`, Palette-`hint`). */
export function shortcutHint(action: ShortcutAction): string {
  return BY_ACTION[action]?.keys[0] ?? "";
}

/** Alle Tasten einer Aktion (für das Overlay). */
export function shortcutKeys(action: ShortcutAction): string[] {
  return BY_ACTION[action]?.keys ?? [];
}

/** Was die Taste TUT, in der eingestellten Sprache. Kein Hook: die Liste wird
 *  auch außerhalb von React gelesen (Palette-Einträge) — `katalog()` liest
 *  denselben Speicher wie `useT()`. */
export function shortcutLabel(action: ShortcutAction): string {
  return katalog().mail.kuerzel.aktion[action];
}

// ── First-Run-Hinweis (X1) ────────────────────────────────────────────────────
// Ob der einmalige „Neu: Alt+Q / F1"-Banner im Posteingang bereits weggeklickt
// wurde. Rein clientseitig (localStorage), KEIN Server-State — schlankes Modul
// nach dem Muster von `ui/undoSendPref.ts` (bewusst KEIN Context-Provider).
export const MAIL_KBD_HINT_SEEN_KEY = "sb_mail_kbd_hint_seen";

export function isMailKbdHintSeen(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem(MAIL_KBD_HINT_SEEN_KEY) === "1";
}

export function setMailKbdHintSeen(): void {
  if (typeof localStorage !== "undefined")
    localStorage.setItem(MAIL_KBD_HINT_SEEN_KEY, "1");
}

// ── First-Run-Hinweis „Rechtsklick" ───────────────────────────────────────────
// Die Listenzeile trägt nur noch Archivieren + Löschen als Symbol; alles andere
// liegt im Kontextmenü. Ein Menü, das niemand vermutet, ersetzt kein Symbol
// (Doc 04 „Kürzel sichtbar machen") — deshalb sagt der Posteingang EINMAL, dass
// der Rechtsklick jetzt trägt. Derselbe Mechanismus wie beim Kürzel-Banner:
// reine UI-Kosmetik, localStorage, kein Server-State und keine Migration. Ein
// eigener Schlüssel (nicht der Kürzel-Schlüssel), sonst verschluckt der eine
// weggeklickte Banner den anderen bei allen Bestandsnutzern.
export const MAIL_ROWMENU_HINT_SEEN_KEY = "sb_mail_rowmenu_hint_seen";

export function isMailRowMenuHintSeen(): boolean {
  if (typeof localStorage === "undefined") return true;
  return localStorage.getItem(MAIL_ROWMENU_HINT_SEEN_KEY) === "1";
}

export function setMailRowMenuHintSeen(): void {
  if (typeof localStorage !== "undefined")
    localStorage.setItem(MAIL_ROWMENU_HINT_SEEN_KEY, "1");
}
